import React, { ReactNode, useEffect, useState } from 'react';
import AuthedLayout from '../../Layouts/Authed';
import { Button, Row, Col, Form, Alert } from 'react-bootstrap';
import { router } from '@inertiajs/react';
import { Link, useForm, usePage } from '@inertiajs/react';
import { getAllCorporateStructures } from '../../Data/CorporateStructure';
import Valuation from '../../Data/Valuation';
import { Role } from '../../Data/Role';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

interface RenewableValuationOption {
	id: number;
	agency_id: number;
	agency_name: string;
	effective_date: string;
}

interface Props {
	valuation: Valuation;
	agencies?: any;
	renewableValuations?: RenewableValuationOption[];
}

const Basics = (props: Props) => {
	const { errors, readOnly: globalReadOnly, user } = usePage<any>().props;
	const isAdmin = user?.role === Role.admin;
	const readOnly = globalReadOnly;
	// Agency name and effective date are admin-only even for non-read-only valuations
	const adminOnlyReadOnly = globalReadOnly || !isAdmin;

	const { data, setData, processing } = useForm({
		new_agency_name: '',
		new_agency_year_founded: '',
		new_agency_corporate_structure: '',
		effective_date: props.valuation.effective_date
			? props.valuation.effective_date
			: '',
		agency_id: props.valuation.agency_id ? props.valuation.agency_id : '',
		renew_from: '' as string | number,
	});
	const autosaveStatus = useValuationStepAutosave(
		'basics',
		props.valuation.id,
		data,
	);

	const submit = (e: any) => {
		e.preventDefault();
		const url = props.valuation.id
			? `/valuation-form/${props.valuation.id}/basics`
			: '/valuation-form/basics';
		router.post(url, data);
	};

	const setAgencyName = (inputValue: any) => {
		if (!inputValue) {
			setData((prev: any) => ({
				...prev,
				agency_id: '',
				new_agency_name: '',
				new_agency_year_founded: '',
				new_agency_corporate_structure: '',
			}));
			setAgencySelect(null);
			setIsCreatingNewAgency(false);
			return;
		}

		if (inputValue.__isNew__) {
			setData((prev: any) => ({
				...prev,
				agency_id: '',
				new_agency_name: inputValue.label,
				new_agency_year_founded: '',
				new_agency_corporate_structure: '',
			}));
			setAgencySelect(inputValue);
			setIsCreatingNewAgency(true);
			return;
		}

		setData((prev: any) => ({
			...prev,
			agency_id: inputValue.value,
			new_agency_name: '',
			new_agency_year_founded: inputValue.year_founded ?? '',
			new_agency_corporate_structure:
				inputValue.corporate_structure ?? '',
		}));
		setAgencySelect(inputValue);
		setIsCreatingNewAgency(false);
		if (inputValue.corporate_structure) {
			const match = getAllCorporateStructures().find(
				(opt: any) =>
					opt.value === inputValue.corporate_structure ||
					opt.label === inputValue.corporate_structure,
			);
			setCorpStructureSelect(match ?? null);
		} else {
			setCorpStructureSelect(null);
		}
	};

	const setCorporateStructure = (inputValue: any) => {
		setData('new_agency_corporate_structure', inputValue?.label ?? '');
		setCorpStructureSelect(inputValue);
	};

	const [agencies, setAgencies]: any = useState();
	const [agencySelect, setAgencySelect]: any = useState();
	const [isCreatingNewAgency, setIsCreatingNewAgency]: any = useState(false);
	const [corpStructureOptions, setCorpStructureOptions]: any = useState();
	const [corpStructureSelect, setCorpStructureSelect]: any = useState();
	const [renewFromSelect, setRenewFromSelect]: any = useState(null);

	const isNewValuation = !props.valuation.id;
	const canRenew =
		isNewValuation &&
		isAdmin &&
		(props.renewableValuations?.length ?? 0) > 0;
	const isRenewing = !!renewFromSelect;

	const renewableOptions = (props.renewableValuations ?? []).map((v) => ({
		value: v.id,
		label: `${v.agency_name} — Effective ${new Date(v.effective_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}`,
		agency_id: v.agency_id,
		effective_date: v.effective_date,
	}));

	const renewalDateOptions = (sourceDateStr?: string) => {
		if (!sourceDateStr) return [];
		const [y, m, d] = sourceDateStr.split('-').map(Number);
		const PL_DATA_LABEL = [
			'2 years of P&L data carried',
			'1 year of P&L data carried',
			'no P&L data; carriers/employees/etc. still carry',
		];
		return [1, 2, 3].map((offset) => {
			const target = new Date(Date.UTC(y + offset, m - 1, d));
			const iso = target.toISOString().slice(0, 10);
			const display = target.toLocaleDateString('en-US', {
				timeZone: 'UTC',
			});
			return {
				value: iso,
				label: `${display} — ${offset} year${offset === 1 ? '' : 's'} later (${PL_DATA_LABEL[offset - 1]})`,
			};
		});
	};

	const [renewalDateSelect, setRenewalDateSelect]: any = useState(null);

	const onRenewFromChange = (option: any) => {
		if (!option) {
			setRenewFromSelect(null);
			setRenewalDateSelect(null);
			setData((prev: any) => ({
				...prev,
				renew_from: '',
				effective_date: '',
			}));
			return;
		}
		setRenewFromSelect(option);
		setRenewalDateSelect(null); // analyst picks the renewal year next

		const sourceAgency = (agencies ?? []).find(
			(a: any) => Number(a.value) === Number(option.agency_id),
		);

		setData((prev: any) => ({
			...prev,
			renew_from: option.value,
			agency_id: option.agency_id,
			effective_date: '',
			new_agency_name: '',
			new_agency_year_founded: sourceAgency?.year_founded ?? '',
			new_agency_corporate_structure:
				sourceAgency?.corporate_structure ?? '',
		}));
		setAgencySelect(sourceAgency ?? null);
		setIsCreatingNewAgency(false);
		if (sourceAgency?.corporate_structure) {
			const match = (corpStructureOptions ?? []).find(
				(opt: any) =>
					opt.value === sourceAgency.corporate_structure ||
					opt.label === sourceAgency.corporate_structure,
			);
			setCorpStructureSelect(match ?? null);
		}
	};

	const onRenewalDateChange = (option: any) => {
		setRenewalDateSelect(option);
		setData('effective_date', option?.value ?? '');
	};

	useEffect(() => {
		let formattedAgencies: any[] = [];
		if (Array.isArray(props.agencies)) {
			formattedAgencies = props.agencies.map((agency: any) => {
				return {
					value: agency.id,
					label: agency.name,
					year_founded: agency.year_founded,
					corporate_structure: agency.corporate_structure,
				};
			});
		} else if (props.agencies) {
			formattedAgencies = Object.keys(props.agencies).map((val: any) => {
				return { value: props.agencies[val], label: val };
			});
		}
		setAgencies(formattedAgencies);

		let formattedCorpStructure = getAllCorporateStructures();
		setCorpStructureOptions(formattedCorpStructure);

		if (props.valuation.id && data.agency_id) {
			const selectedAgency = formattedAgencies.find((agency: any) => {
				return Number(agency.value) === Number(data.agency_id);
			});
			setAgencySelect(selectedAgency);
			setIsCreatingNewAgency(false);
			if (selectedAgency) {
				setData((prev: any) => ({
					...prev,
					new_agency_year_founded: selectedAgency.year_founded ?? '',
					new_agency_corporate_structure:
						selectedAgency.corporate_structure ?? '',
				}));
				if (selectedAgency.corporate_structure) {
					const match = formattedCorpStructure.find(
						(opt: any) =>
							opt.value === selectedAgency.corporate_structure ||
							opt.label === selectedAgency.corporate_structure,
					);
					setCorpStructureSelect(match ?? null);
				}
			}
		}

		scrollTo(0, 0);
	}, []);

	return (
		<div>
			<Row>
				<Col>
					<h1 className='h2'>Basics</h1>
					<hr />
					<InstructionsPanel>
						Fill out the information provided and press 'Next' to go
						to the next tab. Your progress will be automatically
						saved as you proceed. If the data for any section is not
						applicable, leave the fields blank and check the box at
						the bottom of the page.
					</InstructionsPanel>
				</Col>
			</Row>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
					{canRenew && (
						<Row>
							<Form.Group as={Col} md={12} className='py-4'>
								<Form.Label htmlFor='renew_from'>
									Renew from existing valuation{' '}
									<span className='text-muted'>
										(optional)
									</span>
									:
								</Form.Label>
								<ReactSelect
									name='renew_from'
									placeholder='Pick a completed valuation to renew, or leave blank to start fresh'
									value={renewFromSelect}
									onChange={onRenewFromChange}
									options={renewableOptions}
									className='input'
									isClearable
								/>
								{isRenewing && (
									<Form.Text className='text-muted'>
										Agency and effective date will be locked
										to the renewal year. Prior valuation
										data will be carried over when you save.
									</Form.Text>
								)}
							</Form.Group>
						</Row>
					)}
					<Row>
						<Form.Group as={Col} md={6} className='py-4'>
							<Form.Label htmlFor='agency_id'>
								Agency name:
							</Form.Label>
							{errors.agency_id && (
								<Alert variant='dkRed'>
									{errors.agency_id}
								</Alert>
							)}
							{errors.new_agency_name && (
								<Alert variant='dkRed'>
									{errors.new_agency_name}
								</Alert>
							)}
							<CreatableSelect
								name='agency_id'
								placeholder='Agency Name'
								value={agencySelect}
								onChange={setAgencyName}
								options={agencies}
								className='input'
								isClearable
								isDisabled={adminOnlyReadOnly || isRenewing}
							/>
						</Form.Group>
						{agencySelect && (
							<Form.Group as={Col} md={6} className='py-4'>
								<Form.Label htmlFor='new_agency_year_founded'>
									Agency year founded:
								</Form.Label>
								{errors.new_agency_year_founded && (
									<Alert variant='dkRed'>
										{errors.new_agency_year_founded}
									</Alert>
								)}
								<Form.Control
									type='number'
									id='new_agency_year_founded'
									className='input'
									value={data.new_agency_year_founded}
									onChange={(e) =>
										setData(
											'new_agency_year_founded',
											e.target.value,
										)
									}
									disabled={readOnly}
								/>
							</Form.Group>
						)}
					</Row>
					<Row>
						<Form.Group as={Col} md={6} className='py-4'>
							<Form.Label htmlFor='effective_date'>
								Valuation effective date:
							</Form.Label>
							{errors.effective_date && (
								<Alert variant='dkRed'>
									{errors.effective_date}
								</Alert>
							)}
							{isRenewing ? (
								<ReactSelect
									name='effective_date'
									placeholder='Pick the renewal year'
									value={renewalDateSelect}
									onChange={onRenewalDateChange}
									options={renewalDateOptions(
										renewFromSelect?.effective_date,
									)}
									className='input'
								/>
							) : (
								<Form.Control
									type='date'
									id='effective_date'
									className='input'
									value={data.effective_date}
									onChange={(e) =>
										setData(
											'effective_date',
											e.target.value,
										)
									}
									disabled={adminOnlyReadOnly}
								/>
							)}
						</Form.Group>
						{agencySelect && (
							<Form.Group as={Col} md={6} className='py-4'>
								<Form.Label htmlFor='new_agency_corporate_structure'>
									Agency corporate structure:
								</Form.Label>
								{errors.new_agency_corporate_structure && (
									<Alert variant='dkRed'>
										{errors.new_agency_corporate_structure}
									</Alert>
								)}
								<ReactSelect
									options={corpStructureOptions}
									onChange={setCorporateStructure}
									value={corpStructureSelect}
									className='input'
									isDisabled={readOnly}
								/>
							</Form.Group>
						)}
					</Row>
				</fieldset>

				<div className='d-flex justify-content-end align-items-center'>
					{!readOnly && <AutosaveStatus status={autosaveStatus} />}
					{readOnly ? (
						<Button
							as={Link as any}
							href={`/valuation-form/${props.valuation.id}/ownership`}
							className='mt-4 ms-4'
						>
							Next
						</Button>
					) : (
						<Button
							type='submit'
							disabled={processing}
							className='mt-4 ms-4'
						>
							Next
						</Button>
					)}
				</div>
			</Form>
		</div>
	);
};

Basics.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default Basics;
